<script setup>
import { ref, onBeforeMount, onMounted, onUnmounted, computed } from "vue";
import { useRoute } from "vue-router";
import { sideMenuItems, filterMenuItemsForAccess } from "../sideMenuItems";
import { useUiStore } from "@/stores/ui";
import { safeParse } from "@/utils/accessControl";
import router from "@/router";

const userDetails = ref(safeParse(localStorage.getItem("user")));
const superAdminEmail = process.env.VUE_APP_SUPER_ADMIN_EMAIL;
const route = useRoute();
const uiStore = useUiStore();
let menuItems = ref([]);
let openSubmenus = ref(new Set()); // Track which submenus are open
let isHovering = ref(false);

const isCollapsed = computed(() => uiStore.isCollapsed);

const windowWidth = ref(window.innerWidth);
const isMobile = computed(() => windowWidth.value <= 768);

const onResize = () => { windowWidth.value = window.innerWidth; };
onMounted(() => window.addEventListener("resize", onResize));
onUnmounted(() => window.removeEventListener("resize", onResize));

const activeRoute = computed(() => {
  return route.name;
});

// Load collapsed state from localStorage
const loadCollapsedState = () => {
  const saved = localStorage.getItem("sidebarCollapsed");
  if (saved !== null) {
    uiStore.updateCollapseState(saved === "true");
  }
};

// Handle mouse enter
const handleMouseEnter = () => {
  if (isCollapsed.value) {
    isHovering.value = true;
  }
};

// Handle mouse leave
const handleMouseLeave = () => {
  isHovering.value = false;
};

// Handle backdrop click to close sidebar on mobile
const handleBackdropClick = () => {
  isHovering.value = false;
  if (isMobile.value) {
    uiStore.updateCollapseState(true);
    localStorage.setItem("sidebarCollapsed", "true");
  }
};

// Check if sidebar should show expanded
const isExpanded = computed(() => {
  return !isCollapsed.value || isHovering.value;
});

const showBackdrop = computed(
  () => (isExpanded.value && isCollapsed.value) || (isMobile.value && !isCollapsed.value)
);

// Get subitems from either subItems or children property
const getSubItems = (item) => {
  const subItems = item.subItems || item.children || [];
  return subItems;
};

// Get the link for parent item - either from a child with same label or default route
const getParentLink = (item) => {
  const allSubItems = item.subItems || item.children || [];
  // Find child with same label as parent (the main route)
  const mainChild = allSubItems.find(
    (subItem) =>
      subItem.label === item.label ||
      subItem.label === "Agents List" ||
      subItem.label === "Data Library" ||
      subItem.label === "Device Configurations",
  );
  if (mainChild && mainChild.link) {
    return mainChild.link;
  }
  // Fallback to item's own link or construct default
  return item.link || `/${item.label.toLowerCase().replace(/\s+/g, "-")}`;
};

// Toggle submenu open/close
const toggleSubmenu = (item, event) => {
  event.stopPropagation();
  event.preventDefault();

  const key = item.id !== undefined ? item.id : item.label;

  if (openSubmenus.value.has(key)) {
    openSubmenus.value.delete(key);
  } else {
    openSubmenus.value.add(key);
  }
};

// Check if submenu is open
const isSubmenuOpen = (item) => {
  const key = item.id !== undefined ? item.id : item.label;
  return openSubmenus.value.has(key);
};

// Check if current route matches any subitem of a parent
const isParentActive = (item) => {
  const subItems = getSubItems(item);
  if (!subItems.length) return false;
  return subItems.some((subItem) => route.name === subItem.route_name);
};

// Check if submenu item should show as active
const isSubItemActive = (subItem) => {
  return (
    activeRoute.value === subItem.route_name ||
    (activeRoute.value === "configSettings" &&
      subItem.route_name === "ConfigurationsView") ||
    ((activeRoute.value === "themeBuilder" ||
      activeRoute.value === "ThemeAssignmentsView") &&
      subItem.route_name === "ThemesView") ||
    (activeRoute.value === "createAgentInstructionsView" &&
      subItem.route_name === "AgentInstructionsView") ||
    (activeRoute.value === "createNewMedia" &&
      subItem.route_name === "MediaLibraryView") ||
    (activeRoute.value === "createCampaign" &&
      subItem.route_name === "CampaignsView") ||
    (activeRoute.value === "editCampaign" &&
      subItem.route_name === "CampaignsView") ||
    (activeRoute.value === "createDataSource" &&
      subItem.route_name === "DataSourceView") ||
    (activeRoute.value === "editDataSource" &&
      subItem.route_name === "DataSourceView") ||
    (activeRoute.value === "uploadFiles" &&
      subItem.route_name === "DataLibraryView") ||
    (activeRoute.value === "AgentAnalyticsView" &&
      subItem.route_name === "ConversationsAnalyticsView") ||
    (activeRoute.value === "DataMappingFilesView" &&
      subItem.route_name === "IntegrationsView") ||
    (activeRoute.value === "FileDataMappingView" &&
      subItem.route_name === "IntegrationsView")
  );
};

// Check if item should show as active
const isItemActive = (item) => {
  return (
    activeRoute.value === item.route_name ||
    (activeRoute.value === "FileDataMappingView" &&
      item.route_name === "IntegrationsView") ||
    (activeRoute.value === "DataMappingFilesView" &&
      item.route_name === "IntegrationsView") ||
    (activeRoute.value === "configSettings" &&
      item.route_name === "ConfigurationsView") ||
    ((activeRoute.value === "themeBuilder" ||
      activeRoute.value === "ThemeAssignmentsView") &&
      item.route_name === "ThemesView") ||
    (activeRoute.value === "createAgentInstructionsView" &&
      item.route_name === "AgentInstructionsView") ||
    (activeRoute.value === "createNewMedia" &&
      item.route_name === "MediaLibraryView") ||
    (activeRoute.value === "createCampaign" &&
      item.route_name === "CampaignsView") ||
    (activeRoute.value === "editCampaign" &&
      item.route_name === "CampaignsView") ||
    (activeRoute.value === "createDataSource" &&
      item.route_name === "DataSourceView") ||
    (activeRoute.value === "editDataSource" &&
      item.route_name === "DataSourceView") ||
    (activeRoute.value === "uploadFiles" &&
      item.route_name === "DataLibraryView") ||
    (activeRoute.value === "AgentAnalyticsView" &&
      item.route_name === "ConversationsAnalyticsView") ||
    isParentActive(item)
  );
};

// Initialize open submenus based on current route
const initializeOpenSubmenus = () => {
  menuItems.value.forEach((item) => {
    const subItems = getSubItems(item);
    const key = item.id !== undefined ? item.id : item.label;

    // Check direct children
    if (
      subItems.length &&
      subItems.some((subItem) => route.name === subItem.route_name)
    ) {
      openSubmenus.value.add(key);
    }

    // Check grandchildren
    subItems.forEach((subItem) => {
      const grandChildren = getSubItems(subItem);
      if (
        grandChildren.length &&
        grandChildren.some((gc) => route.name === gc.route_name)
      ) {
        openSubmenus.value.add(key); // Open parent
        openSubmenus.value.add(subItem.label); // Open child (using label as key for level 2)
      }
    });

    // Handle special routes that are children of parent routes
    if (subItems.length) {
      const shouldOpenForSpecialRoute =
        (route.name === "configSettings" &&
          subItems.some((sub) => sub.route_name === "ConfigurationsView")) ||
        ((route.name === "themeBuilder" ||
          route.name === "ThemeAssignmentsView") &&
          subItems.some((sub) => sub.route_name === "ThemesView")) ||
        (route.name === "createAgentInstructionsView" &&
          subItems.some((sub) => sub.route_name === "AgentInstructionsView")) ||
        (route.name === "createNewMedia" &&
          subItems.some((sub) => sub.route_name === "MediaLibraryView")) ||
        (route.name === "createCampaign" &&
          subItems.some((sub) => sub.route_name === "CampaignsView")) ||
        (route.name === "editCampaign" &&
          subItems.some((sub) => sub.route_name === "CampaignsView")) ||
        (route.name === "createDataSource" &&
          subItems.some((sub) => sub.route_name === "DataSourceView")) ||
        (route.name === "editDataSource" &&
          subItems.some((sub) => sub.route_name === "DataSourceView")) ||
        (route.name === "uploadFiles" &&
          subItems.some((sub) => sub.route_name === "DataLibraryView")) ||
        (route.name === "DataMappingFilesView" &&
          subItems.some((sub) => sub.route_name === "IntegrationsView")) ||
        (route.name === "FileDataMappingView" &&
          subItems.some((sub) => sub.route_name === "IntegrationsView")) ||
        (route.name === "AgentAnalyticsView" &&
          subItems.some(
            (sub) => sub.route_name === "ConversationsAnalyticsView",
          ));

      if (shouldOpenForSpecialRoute) {
        openSubmenus.value.add(key);
      }
    }
  });
};

onBeforeMount(() => {
  if (!userDetails.value) {
    localStorage.clear();
    router.push("/login");
    return;
  }

  // On mobile always start collapsed (sidebar hidden as drawer)
  if (window.innerWidth <= 768) {
    uiStore.updateCollapseState(true);
    localStorage.setItem("sidebarCollapsed", "true");
  } else {
    loadCollapsedState();
  }

  menuItems.value = filterMenuItemsForAccess(
    sideMenuItems,
    userDetails.value,
    superAdminEmail,
  );

  // Initialize open submenus based on current route
  initializeOpenSubmenus();

});
</script>

<template>
  <div>
    <!-- Backdrop overlay for mobile -->
    <div
      v-if="showBackdrop"
      class="sidebar-backdrop"
      @click="handleBackdropClick"
      @touchend.prevent="handleBackdropClick"
    ></div>

    <aside
      class="sidebar"
      :class="{ collapsed: isCollapsed, expanded: isExpanded }"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
<div class="sidebar-inner">
        <nav class="sidebar-nav">
          <ul class="pl-0">
            <li
              v-for="item in menuItems"
              :key="item.id || item.label"
              :class="[
                { active: isItemActive(item) },
                'sidebar-item-' + item.id,
              ]"
            >
              <!-- Parent item with subitems -->
              <div v-if="getSubItems(item).length > 0">
                <!-- Parent item main content - clickable for navigation -->
                <div
                  class="nav-item parent-item"
                  :class="{
                    'has-subitems': true,
                    'submenu-open': isSubmenuOpen(item),
                    active: activeRoute === item.route_name,
                  }"
                  @click="$router.push(getParentLink(item))"
                >
                  <span class="nav-icon">
                    <i v-html="item.svg"></i>
                  </span>
                  <span class="nav-text" v-show="isExpanded">{{
                    item.label
                  }}</span>

                  <!-- Arrow icon for submenu toggle -->
                  <span
                    v-show="isExpanded"
                    class="submenu-arrow"
                    :class="{ rotated: isSubmenuOpen(item) }"
                    @click.stop="toggleSubmenu(item, $event)"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path
                        d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"
                      />
                    </svg>
                  </span>
                </div>

                <!-- Submenu items -->
                <ul
                  v-if="isSubmenuOpen(item) && isExpanded"
                  class="submenu-items"
                >
                  <li
                    v-for="subItem in getSubItems(item)"
                    :key="subItem.id || subItem.label"
                    class="submenu-item"
                    :class="{ active: isSubItemActive(subItem) }"
                  >
                    <!-- Check if subItem has children -->
                    <div v-if="getSubItems(subItem).length > 0">
                      <div
                        class="nav-item sub-nav-item parent-item"
                        :class="{ 'submenu-open': isSubmenuOpen(subItem) }"
                        @click="$router.push(getParentLink(subItem))"
                      >
                        <span class="nav-icon sub-icon">
                          <i v-html="subItem.svg"></i>
                        </span>
                        <span class="nav-text sub-text" v-show="isExpanded">{{
                          subItem.label
                        }}</span>

                        <!-- Arrow for 2nd level -->
                        <span
                          v-show="isExpanded"
                          class="submenu-arrow"
                          :class="{ rotated: isSubmenuOpen(subItem) }"
                          @click.stop="toggleSubmenu(subItem, $event)"
                        >
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path
                              d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"
                            />
                          </svg>
                        </span>
                      </div>

                      <!-- 3rd Level Menu -->
                      <ul
                        v-if="isSubmenuOpen(subItem) && isExpanded"
                        class="submenu-items level-3"
                      >
                        <li
                          v-for="grandChild in getSubItems(subItem)"
                          :key="grandChild.label"
                          class="submenu-item"
                          :class="{ active: isSubItemActive(grandChild) }"
                          @click.stop="$router.push(grandChild.link)"
                        >
                          <div class="nav-item sub-nav-item">
                            <span class="nav-icon sub-icon">
                              <i v-html="grandChild.svg"></i>
                            </span>
                            <span
                              class="nav-text sub-text"
                              v-show="isExpanded"
                              >{{ grandChild.label }}</span
                            >
                          </div>
                        </li>
                      </ul>
                    </div>

                    <!-- Regular subitem (no children) -->
                    <div
                      v-else
                      class="nav-item sub-nav-item"
                      @click="$router.push(subItem.link)"
                    >
                      <span class="nav-icon sub-icon">
                        <i v-html="subItem.svg"></i>
                      </span>
                      <span class="nav-text sub-text" v-show="isExpanded">{{
                        subItem.label
                      }}</span>
                    </div>
                  </li>
                </ul>
              </div>

              <!-- Regular menu item (no subitems) -->
              <div v-else class="nav-item" @click="$router.push(item.link)">
                <span class="nav-icon">
                  <i v-html="item.svg"></i>
                </span>
                <span class="nav-text" v-show="isExpanded">{{
                  item.label
                }}</span>
              </div>
            </li>
          </ul>
        </nav>
      </div>
    </aside>
  </div>
</template>

<style lang="scss" scoped>
.sidebar-backdrop {
  display: none;
  position: fixed;
  top: 64px;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  transition: opacity 0.3s ease;

  @media (max-width: 1024px) {
    display: block;
  }
}

.sidebar {
  width: 260px;
  height: 100%;
  /* background-color: white; Moved to Inner */
  /* border-right: 1px solid var(--neutral-200); Moved to Inner */
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease; /* Removed transform transition */
  position: relative;
  z-index: 1000;

  &.collapsed {
    width: 70px;
  }

  /* Removed .collapsed.expanded width override to prevent layout shift */

  @media (max-width: 1024px) {
    position: fixed;
    left: 0;
    top: 64px;
    height: calc(100vh - 64px);
    width: 70px;
    box-shadow: none; /* Shadow handled by inner */

    &.collapsed {
      width: 70px;
    }

    &.expanded {
      width: 70px; /* Keep container width same, inner expands */
      z-index: 1001;
    }
  }

  @media (max-width: 768px) {
    /* Override the 1024px icon-strip widths with equal specificity */
    &.collapsed {
      width: 280px;
      max-width: 82vw;
      transform: translateX(-100%);
      transition: transform 0.3s ease;
    }

    &.expanded,
    &:not(.collapsed) {
      width: 280px;
      max-width: 82vw;
      transform: translateX(0);
      transition: transform 0.3s ease;
    }
  }
}

/* ── Modern sidebar design ─────────────────────────────────── */
.sidebar-inner {
  background-color: var(--neutral-0);
  border-right: 1px solid var(--neutral-200);
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  height: 100%;
  transition: width 0.3s ease, box-shadow 0.3s ease;
  overflow: hidden;
  white-space: nowrap;

  .sidebar.collapsed.expanded & {
    width: 260px;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    box-shadow: 4px 0 18px rgba(15, 23, 42, 0.08);
    z-index: 1002;
  }

  .sidebar.collapsed:not(.expanded) & {
    .user-avatar {
      margin-right: 0;
    }
  }

  @media (max-width: 1024px) {
    .sidebar.expanded & {
      width: 260px;
      box-shadow: 4px 0 18px rgba(15, 23, 42, 0.12);
    }
  }

  @media (max-width: 768px) {
    /* Match the specificity of the 1024px .sidebar.expanded rule */
    .sidebar.expanded & {
      width: 100%;
      box-shadow: 4px 0 24px rgba(15, 23, 42, 0.15);
    }

    .sidebar:not(.collapsed) & {
      width: 100%;
      box-shadow: 4px 0 24px rgba(15, 23, 42, 0.15);
    }

    .sidebar.collapsed & {
      width: 100%;
      box-shadow: none;
    }
  }
}

.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  padding: 12px 10px 16px;

  /* Subtle scrollbar */
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: var(--border-color);
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb:hover { background: var(--neutral-300); }

  ul {
    list-style-type: none;
    margin: 0;
    padding: 0;
  }

  > ul > li {
    cursor: pointer;
    margin-bottom: 2px;

    /* No level-1 li hover background — handled on .nav-item itself */
    &:hover { background: transparent; }

    /* Active level-1 — tinted pill + colored icon, no left border */
    &.active > .nav-item {
      background: linear-gradient(
        180deg,
        rgba(47, 77, 132, 0.08) 0%,
        rgba(47, 77, 132, 0.04) 100%
      );
      box-shadow: inset 0 0 0 1px rgba(47, 77, 132, 0.1);

      .nav-text { color: var(--primary-color); font-weight: 600; }
      .nav-icon { color: var(--primary-color); }
    }
  }

  /* ── Level-1 nav item ───────────────────────────────────── */
  .nav-item {
    position: relative;
    padding: 10px 12px;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 12px;
    border-radius: 10px;
    transition:
      background-color 0.15s ease,
      color 0.15s ease,
      box-shadow 0.15s ease;
    cursor: pointer;

    &:hover {
      background-color: var(--neutral-50);

      .nav-icon { color: var(--neutral-800); }
      .nav-text { color: var(--neutral-900); }
    }

    @media (max-width: 1024px) {
      padding: 10px 8px;
      justify-content: center;

      .sidebar.expanded & {
        padding: 10px 12px;
        justify-content: flex-start;
      }
    }

    @media (max-width: 768px) {
      .sidebar.expanded &,
      .sidebar:not(.collapsed) & {
        padding: 10px 12px;
        justify-content: flex-start;
      }

      /* When drawer is closed, center icons */
      .sidebar.collapsed & {
        justify-content: center;
      }
    }
  }

  /* Parent (has subitems) */
  .parent-item {
    cursor: pointer;
    justify-content: space-between;

    &.submenu-open {
      background-color: var(--neutral-50);

      .nav-text { color: var(--neutral-900); font-weight: 600; }
      .nav-icon { color: var(--primary-color); }
    }
  }

  /* Submenu arrow */
  .submenu-arrow {
    width: 22px;
    height: 22px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    color: var(--neutral-400);
    transition: transform 0.2s ease, background-color 0.15s ease, color 0.15s ease;

    &:hover {
      background: var(--neutral-100);
      color: var(--primary-color);
    }

    &.rotated { transform: rotate(90deg); }
  }

  /* ── Submenu container (level 2) ────────────────────────── */
  .submenu-items {
    margin: 4px 0 6px 18px;
    padding: 4px 0 4px 16px;
    border-left: 1px solid var(--neutral-200);
    background: transparent;

    .submenu-item {
      position: relative;
      margin-bottom: 1px;

      /* Connector dot */
      &::before {
        content: "";
        position: absolute;
        left: -19px;
        top: 50%;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: var(--neutral-300);
        transform: translateY(-50%);
        transition: background-color 0.15s ease;
      }

      &:hover::before { background: var(--primary-color); }

      &.active {
        background: transparent;
        border-left: 0;

        &::before { background: var(--primary-color); }

        > .nav-item,
        > .sub-nav-item,
        > div > .sub-nav-item {
          background: rgba(47, 77, 132, 0.08);

          .sub-text { color: var(--primary-color); font-weight: 600; }
          .sub-icon { color: var(--primary-color); }
        }
      }
    }
  }

  /* Submenu item nav row */
  .sub-nav-item {
    padding: 7px 5px;
    border-radius: 8px;
    gap: 10px;

    &:hover {
      background: var(--neutral-50);
      .sub-icon { color: var(--neutral-800); }
      .sub-text { color: var(--neutral-900); }
    }

    .sub-icon {
      width: 18px;
      height: 18px;
      color: var(--neutral-500);
    }

    .sub-text {
      font-size: 0.8125rem;
      color: var(--neutral-600);
    }
  }

  /* Level-3 nesting */
  .submenu-items.level-3 {
    margin-left: 14px;
    padding-left: 12px;
    border-left: 1px dashed var(--neutral-200);

    .submenu-item {
      &::before {
        left: -17px;
        width: 5px;
        height: 5px;
      }

      &.active::before { background: var(--primary-color); }
    }

    .sub-nav-item { padding-left: 14px; }
  }

  /* ── Icons ───────────────────────────────────────────────── */
  .nav-icon {
    width: 22px;
    height: 22px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--neutral-500);
    flex-shrink: 0;
    transition: color 0.15s ease;

    :deep(svg) {
      width: 18px;
      height: 18px;
    }
  }

  .nav-text {
    color: var(--neutral-600);
    font-size: 0.875rem;
    font-weight: 500;
    flex: 1;
    transition: color 0.15s ease, font-weight 0.15s ease;
  }
}


.sidebar-footer {
  padding: 14px 16px;
  border-top: 1px solid var(--neutral-200);

  .user-info {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .user-avatar {
    width: 36px;
    height: 36px;
    background: linear-gradient(135deg, #e0ecff 0%, #c7d2fe 100%);
    color: var(--primary-hover);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    margin-right: 4px;
  }

  .user-details {
    display: flex;
    flex-direction: column;
    line-height: 1.3;

    .user-name {
      font-weight: 600;
      font-size: 0.875rem;
      color: var(--neutral-900);
    }

    .user-role {
      font-size: 0.75rem;
      color: var(--neutral-400);
    }
  }
}
</style>
